import React, { useEffect, useMemo, useState, memo } from 'react';
import { ChildSelectorBar } from '../components/ChildSelectorBar';
import { Trophy, Star, Clock, ArrowRight, Link as LinkIcon, TrendingUp, Lightbulb, Target, Flame, AlertTriangle, FileText, CalendarDays, History, Users, CheckCircle2, CloudOff } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { DjayNarrator, DjayBubble } from '../../../mascot';
import { MODULES, getModuleIcon } from '../../../content';
import { getWeeklyStats } from '../../../utils/activityHelpers';
import { getPracticeDaysThisWeek } from '../../../utils/streakFacts';
import { levelCodeToCitoLevel } from '../../../utils/citoLevelUtils';
import { type LearnerAccount, type ParentAccount, type AccountsState, LINK_CODE_LENGTH, LINK_CODE_EXAMPLE } from '../../../accounts';
import { AppState } from '../../../app/types';
import { ParentV2Route } from '../ParentSidebarV2';
import { useModuleMastery } from '../hooks/useModuleMastery';
import { useWeeklyActivity } from '../hooks/useWeeklyActivity';
import { useChildDecay } from '../hooks/useChildDecay';
import { useLatestAssessment } from '../hooks/useLatestAssessment';
import { useLinkChild } from '../hooks/useLinkChild';
import { NoChildDataNotice } from '../components/NoChildDataNotice';
import { DataFreshness } from '../../shared/DataFreshness';
import { Skeleton } from '../../../components/shared/Skeleton';
import { getChildLastActivity } from '../shared/childActivity';
import { useRefreshChild } from '../hooks/useRefreshChild';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { getParentTipDismissals, isTipDismissed } from '../../../data/domains/preferences';
interface HomePageProps {
  accounts: AccountsState;
  activeAccount: ParentAccount;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  setViewingLearner: (id: string) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
  /** De kinderlijst kon niet bij de server worden gecontroleerd. */
  linkedListUnavailable?: boolean;
}
/**
 * Parent portal Home — 10-second dashboard.
 *
 * Stat-ownership (audit §16):
 * - Level + XP + current streak → ONLY here (compact hero chips)
 * - Weekly rhythm → compact strip here; canonical detail lives on Activity
 * - Decay alerts → top 2 here; full list on Tips
 * - Top tip → 1 here; full list on Tips
 * - Latest assessment → compact chip here; detail on Vakken (Toetsen-route in Wave 3)
 * - Overall mastery / module details → NOT shown here (canonical: Vakken)
 * - Days/Q/Accuracy this week → NOT shown here (canonical: Activity)
 *
 * Anti-pattern removed (Wave 2): quick-nav stat cards that re-rendered
 * "Gemiddelde beheersing" and "Oefendagen 3/7" — these duplicated the
 * canonical pages a click away. Replaced with a slim explore-row.
 */
export const HomePage = memo(function HomePage({
  activeAccount,
  linkedLearners,
  selectedChild,
  childData,
  selectedGroup,
  getLearnerModuleMastery,
  setViewingLearner,
  // `linkParentToLearnerById` komt nog binnen via de gedeelde prop-spread van
  // ParentShellV2 (SettingsPage gebruikt het voor "ongedaan maken" na
  // ontkoppelen), maar dit scherm koppelt niet meer lokaal: dat doet de server.
  onNavigate,
  linkedListUnavailable
}: HomePageProps) {
  // All hooks MUST be called before any early return
  const {
    refresh
  } = useRefreshChild(selectedChild ? [selectedChild] : []);
  // W4-6: shared hook replaces inline useMemo (audit C-P-6)
  const {
    weeklyActivity
  } = useWeeklyActivity(childData);
  const weeklyStats = useMemo(() => getWeeklyStats(childData?.questionStates || {}), [childData?.questionStates]);
  const moduleMastery = useModuleMastery(selectedChild, getLearnerModuleMastery);
  // v1.6: decay-iteration extracted to useChildDecay hook (audit C-P-NEW-3).
  // Single source of truth for the family↔module mapping heuristic.
  const decay = useChildDecay(childData);
  // W4-2: respect parent-scoped tip dismissals on the Home preview slot.
  // Loaded async on mount + when the parent changes; empty map until ready.
  const [tipDismissals, setTipDismissals] = useState<Record<string, number>>({});
  useEffect(() => {
    let cancelled = false;
    void getParentTipDismissals(activeAccount.id).then(next => {
      if (!cancelled) setTipDismissals(next);
    });
    return () => {
      cancelled = true;
    };
  }, [activeAccount.id]);
  // Decay alerts — max 2, stale only. Same data as TipsPage but capped tighter.
  const decayAlerts = useMemo(() => {
    if (!selectedChild) return [];
    const alerts: Array<{
      module: (typeof MODULES)[number];
      daysSince: number;
      status: 'fading' | 'stale';
    }> = [];
    for (const module of MODULES) {
      const mastery = getLearnerModuleMastery(selectedChild.id, module.id);
      if (mastery <= 0) continue;
      const info = decay.perModule[module.id];
      if (!info || info.worstStatus !== 'stale') continue;
      alerts.push({
        module,
        daysSince: info.daysSince,
        status: info.worstStatus
      });
    }
    return alerts.sort((a, b) => b.daysSince - a.daysSince).slice(0, 2);
  }, [selectedChild?.id, decay.perModule, getLearnerModuleMastery]);
  // Top tip — most actionable single signal
  const topTip = useMemo(() => {
    if (!selectedChild || !childData) return null;
    const daysPracticed = weeklyActivity.filter(d => d.practiced).length;
    // Candidates share IDs with TipsPage so dismissals stay in sync.
    if (daysPracticed === 0 && !isTipDismissed(tipDismissals, 'rhythm-none')) {
      return {
        title: 'Start met een vast ritme',
        body: `Korte, dagelijkse sessies van 10 minuten werken beter dan één lange sessie.`,
        accent: 'amber' as const
      };
    }
    const weakest = moduleMastery.filter(m => m.mastery < 50).sort((a, b) => a.mastery - b.mastery)[0];
    if (weakest && !isTipDismissed(tipDismissals, `subject-weak-${weakest.module.id}`)) {
      return {
        title: `Extra aandacht voor ${weakest.module.title.toLowerCase()}`,
        body: `${selectedChild.displayName} oefent dit vak nog op ${weakest.mastery}%. Meester djay past het tempo automatisch aan.`,
        accent: 'rose' as const
      };
    }
    // Zie streakFacts: dit las een niet-bestaand veld en stond dus altijd op 0.
    const currentStreak = getPracticeDaysThisWeek(childData);
    if (currentStreak >= 3 && !isTipDismissed(tipDismissals, 'streak-active')) {
      return {
        title: `${currentStreak} dagen op rij geoefend!`,
        body: `Een mooie streak. Stimuleer ${selectedChild.displayName} om de reeks vast te houden.`,
        accent: 'amber' as const
      };
    }
    return {
      title: 'Persoonlijke tips voor jullie',
      body: `Bekijk advies op maat over ritme, vakken en motivatie.`,
      accent: 'emerald' as const
    };
  }, [selectedChild?.id, childData, weeklyActivity, moduleMastery, tipDismissals]);
  // Latest assessment (compact chip — full detail on Vakken / future Toetsen route)
  // v1.6: extracted to useLatestAssessment hook (audit F-RUN-NEW-5).
  // v1.7: consume isLoading to render skeleton instead of flashing the empty
  // placeholder card on every page mount (audit P-2).
  const {
    report: latestAssessment,
    isLoading: isLoadingAssessment,
    loadError: assessmentLoadError
  } = useLatestAssessment(selectedChild, selectedGroup);
  // Link child hook for empty state
  const {
    linkCode,
    setLinkCode,
    error,
    success,
    notice,
    isSubmitting,
    handleSubmit
  } = useLinkChild();
  /*
   Een gekoppeld kind zonder lokale oefengegevens is GEEN ongekoppeld kind.
   Deze voorwaarde bevatte `|| !childData`, waardoor een ouder die zojuist
   succesvol had gekoppeld het koppelcodeformulier terugkreeg — met de naam
   van datzelfde kind zichtbaar bij Toetsen en Instellingen. Zie
   NoChildDataNotice voor waarom die twee gegevens uit elkaar lopen.
   */
  if (selectedChild && !childData) {
    return <PageLayout title="Welkom!" description={`Fijn dat je er bent, ${activeAccount.displayName}`} data-id="element-8620" data-mp="3ozohbocpunnl">
        <div className="mt-12" data-id="element-8621" data-mp="3szyvvpekqme2">
          <NoChildDataNotice childName={selectedChild.displayName} whatAppearsHere={`Zodra ${selectedChild.displayName} weer oefent, zie je hier hoe het gaat.`} data-id="element-8622" data-mp="3sz5l8dchvcqn" />
        </div>
      </PageLayout>;
  }
  // Empty state — no children linked
  if (linkedLearners.length === 0 || !selectedChild) {
    return <PageLayout title="Welkom!" description={`Fijn dat je er bent, ${activeAccount.displayName}`} data-id="element-8623" data-mp="23nd8c4vegrj6">
        
        <div className="max-w-2xl mx-auto mt-12" data-id="element-8624" data-mp="28l597oe0vnzi">
          <div className="flex flex-col items-center mb-8" data-id="element-8625" data-mp="211vx3ne4tb6r">
            <DjayNarrator size="lg" emotion="happy" animate data-id="element-8626" data-mp="21373luyyox1k" />
            <div className="mt-6 w-full max-w-sm" data-id="element-8627" data-mp="1w9vu1m2rm25y">
              <DjayBubble message="Hoi! Ik ben Djay. Laten we samen de voortgang van je kind volgen!" position="top" colorScheme="emerald" autoDismissMs={0} data-id="element-8628" data-mp="1rbkm5gw161qd" />
              
            </div>
          </div>
          {/*
           "Geen kinderen" en "we konden het niet controleren" leidden tot
           precies hetzelfde scherm: dit koppelformulier, met de mededeling
           dat er nog geen kind gekoppeld is. Voor een ouder die er wél één
           heeft — maar bij wie de serveraanroep faalde — is dat een onware
           bewering, en ze stuurt hem naar een code die hij misschien niet
           meer heeft. Het formulier blijft staan (opnieuw koppelen is
           onschadelijk, dat meldt netjes "was al gekoppeld"), maar de
           aanname erboven wordt teruggenomen.
           */}
          {linkedListUnavailable && <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-left" role="status" data-id="element-8629" data-mp="3qypjz5aaix5z">
              <p className="text-sm font-bold text-amber-900" data-id="element-8630" data-mp="3m1pa0jabla9l">
                We konden je koppelingen even niet ophalen
              </p>
              <p className="text-sm text-amber-800 leading-relaxed mt-1" data-id="element-8631" data-mp="4lpn12ipjz2v">
                Er staat dus niet dat je geen kind gekoppeld hebt — we weten
                het op dit moment niet. Ververs de pagina om het opnieuw te
                proberen.
              </p>
            </div>}
          <Card className="p-8 border-emerald-100 shadow-sm text-center bg-white" data-id="element-8632" data-mp="9l27xhq0qw2t">
            {success ? <div className="py-8 animate-in fade-in zoom-in duration-300" data-id="element-8633" data-mp="nhz500wqd9s5">
                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8634" data-mp="ikyy5ns242zz">
                  <CheckCircle2 className="w-8 h-8" data-id="element-8635" data-mp="2pbygondrlt5d" />
                </div>
                <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-8636" data-mp="2kcvav0pyniwc">
                  Gelukt!
                </h2>
                <p className="text-emerald-700 font-medium" data-id="element-8637" data-mp="zvcdxsc6ae89">{success}</p>

                {/* Er kijkt al iemand anders mee. Geen fout, wel iets dat een
                   ouder hoort te weten — en dus niet in het groene vlak, dat
                   leest als "klaar, niets meer te zien". */}
                {notice && <div className="mt-6 mx-auto max-w-sm p-4 bg-amber-50 border border-amber-200 rounded-xl text-left flex gap-3" data-id="element-8638" data-mp="uzx2t85juwfj">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-8639" data-mp="m24nw53qpqso" />
                    <p className="text-sm text-amber-800" data-id="element-8640" data-mp="74uc0un6qiwf">{notice}</p>
                  </div>}
              </div> : <div className="animate-in fade-in duration-300" data-id="element-8641" data-mp="c2vtjb4yqa5s">
                <h2 className="text-2xl font-black text-slate-900 mb-3" data-id="element-8642" data-mp="c5i47jn5h5s8">
                  Koppel je kind
                </h2>
                {/*
                 Het aantal tekens stond hier als los getal in de lopende tekst
                 — en bleef op 6 staan toen de code naar 8 ging. Een ouder telt
                 dan na, komt op 8, en concludeert dat hij de verkeerde code
                 heeft. Precies de twijfel die het invoerveld ernaast juist
                 moet wegnemen.
                 */}
                <p className="text-slate-600 mb-8 max-w-md mx-auto" data-id="element-8643" data-mp="1r0hzvp3xskmx">
                  Vul de koppelcode van je kind in om de voortgang te kunnen
                  volgen. Je kind vindt deze code van {LINK_CODE_LENGTH} tekens
                  in zijn of haar instellingen; een leerkracht kan hem ook
                  geven.
                </p>
                <form onSubmit={handleSubmit} className="max-w-xs mx-auto" data-id="element-8644" data-mp="2dzmvj5sqdvwq">
                  <div className="space-y-4" data-id="element-8645" data-mp="29zvu885kvxdy">
                    {/*
                     Lengte én voorbeeld komen uit één bron. Hier stond een
                     hardgecodeerd voorbeeld "A1B2C3D4" met een `1` erin,
                     terwijl het alfabet juist géén 0, 1, I, L, O of S bevat —
                     die zijn eruit gelaten omdat een kind deze code vaak
                     VOORLEEST aan een ouder. Het voorbeeld toonde dus een code
                     die nooit kan bestaan, en een ouder die zijn eigen code
                     ermee vergeleek concludeerde dat hij de verkeerde had.
                     */}
                    <Input label="Koppelcode" value={linkCode} onChange={e => setLinkCode(e.target.value.toUpperCase())} placeholder={`Bijv. ${LINK_CODE_EXAMPLE}`} className="text-center text-lg tracking-widest uppercase font-mono bg-slate-50" maxLength={LINK_CODE_LENGTH} autoComplete="off" autoCapitalize="characters" spellCheck={false} data-id="element-8646" data-mp="1298egi6qpc44" />
                  
                    {error && <p className="text-sm text-rose-600 font-medium text-left" data-id="element-8647" data-mp="1kyeo7fpk2tds">
                        {error}
                      </p>}
                    <Button type="submit" disabled={isSubmitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl" data-id="element-8648" data-mp="1pz2razr270od">
                    
                      <LinkIcon className="w-4 h-4 mr-2" data-id="element-8649" data-mp="1h8kf0iqewy4o" />
                      {isSubmitting ? 'Bezig met koppelen…' : 'Koppelen'}
                    </Button>
                  </div>
                </form>
              </div>}
          </Card>
        </div>
      </PageLayout>;
  }
  // Djay greeting selection
  const getDjayMessage = () => {
    if (weeklyStats.practiceDays >= 3) {
      return {
        emotion: 'proud' as const,
        message: `Wat een topweek! ${selectedChild.displayName} heeft al ${weeklyStats.practiceDays} dagen geoefend.`
      };
    }
    if (moduleMastery.length > 0 && moduleMastery[0].mastery > 70) {
      return {
        emotion: 'happy' as const,
        message: `${selectedChild.displayName} is super goed bezig met ${moduleMastery[0].module.title.toLowerCase()}!`
      };
    }
    if (weeklyStats.practiceDays === 0) {
      return {
        emotion: 'encouraging' as const,
        message: `Tijd voor een oefensessie? 15 minuutjes per dag maakt al een groot verschil!`
      };
    }
    return {
      emotion: 'happy' as const,
      message: `Hier is het overzicht van hoe het gaat met ${selectedChild.displayName}.`
    };
  };
  const djayMessage = getDjayMessage();
  const currentStreak = getPracticeDaysThisWeek(childData);
  const cito = latestAssessment ? levelCodeToCitoLevel(latestAssessment.overallEstimatedLevelCode, latestAssessment.selectedGroup) : null;
  return <PageLayout title="Overzicht" description="Hoe gaat het deze week?" actions={<DataFreshness updatedAt={getChildLastActivity(childData)} onRefresh={refresh} data-id="element-8651" data-mp="39x1sa3gbrfmr" />} data-id="element-8650" data-mp="34zjsp3o76crf">
      
      <div className="max-w-5xl mx-auto space-y-8 pb-12" data-id="element-8652" data-mp="2w7hsn6i1iyho">
        <ChildSelectorBar linkedLearners={linkedLearners} selectedChildId={selectedChild.id} onSelect={setViewingLearner} data-id="element-8653" data-mp="2w4vlv92z4n0i" />
        

        {/* Hero — solid emerald, compact Djay, three chips (Level / XP / Streak) */}
        <Card className="overflow-hidden border-none shadow-md bg-emerald-600 relative" data-id="element-8654" data-mp="3sp4dzt7tpm3v">
          <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} data-id="element-8655" data-mp="6ex9jzacz7u7" />
          
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6" data-id="element-8656" data-mp="3titu3hz69kxf">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20 shadow-inner flex-shrink-0" data-id="element-8657" data-mp="3ok0la2utyi3l">
              <DjayNarrator size="md" emotion={djayMessage.emotion} data-id="element-8658" data-mp="3eugwyhoclw1c" />
            </div>
            <div className="flex-1 text-center sm:text-left pt-1" data-id="element-8659" data-mp="3at5h33va0p6c">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2" data-id="element-8660" data-mp="3arauzm89qr0o">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight" data-id="element-8661" data-mp="35vvgqt6cmt3q">
                  Hoe gaat het met {selectedChild.displayName}?
                </h1>
                <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap" data-id="element-8662" data-mp="2vx61z99r1orp">
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8663" data-mp="2lr670smco2np">
                    <Star className="w-3 h-3 fill-current" data-id="element-8664" data-mp="2qt3vq2dirpo1" /> Lvl{' '}
                    {childData.stats?.level || 1}
                  </span>
                  <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8665" data-mp="30pqfsoy0ucp0">
                    <Trophy className="w-3 h-3" data-id="element-8666" data-mp="22zlokjfxlao0" /> {childData.stats?.xp || 0} XP
                  </span>
                  {currentStreak > 0 && <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-lg backdrop-blur-sm flex items-center gap-1" data-id="element-8667" data-mp="2czw4ibtcow6v">
                      <Flame className="w-3 h-3" data-id="element-8668" data-mp="2hre5v0zacysd" /> {currentStreak} dag
                      {currentStreak === 1 ? '' : 'en'}
                    </span>}
                </div>
              </div>
              <p className="text-emerald-50 text-lg font-medium max-w-2xl leading-relaxed" data-id="element-8669" data-mp="1p99x8mr67fxx">
                {djayMessage.message}
              </p>
            </div>
          </div>
        </Card>

        {/* Compact weekly rhythm — minimal version; canonical detail on Activity */}
        <section data-id="element-8670" data-mp="3dfh8dfvwnr57">
          <div className="flex items-center justify-between mb-3" data-id="element-8671" data-mp="3dfzdl20jpfp9">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-8672" data-mp="38g4iem9qm3ye">
              <Clock className="w-4 h-4 text-emerald-500" data-id="element-8673" data-mp="3meugo58am9oc" />
              Deze week
            </h2>
            <button onClick={() => onNavigate?.('activiteit')} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-id="element-8674" data-mp="3ieu30mt0q2ic">
              
              Volledige kalender
              <ArrowRight className="w-3 h-3" data-id="element-8675" data-mp="3r62t7a4x6b5q" />
            </button>
          </div>
          <Card className="p-4 border-slate-200 shadow-sm bg-white" data-id="element-8676" data-mp="9xphy5xf3f94">
            <div className="flex justify-between items-center" data-id="element-8677" data-mp="9vuv2m2l5d2e">
              {weeklyActivity.map((day, i) => <div key={i} className="flex flex-col items-center gap-1.5 flex-1 min-w-0" data-id="element-8678" data-mp="4xb8cjg63x8z" data-mpi={i}>
                
                  <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-8679" data-mp="smj6sub342wc">
                    {day.label}
                  </div>
                  <div role="img" aria-label={day.practiced ? `${day.label}: ${day.questionCount} vragen geoefend${day.isToday ? ' (vandaag)' : ''}` : `${day.label}: nog niet geoefend${day.isToday ? ' (vandaag)' : ''}`} className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-[11px] font-black transition-all ${day.practiced ? 'bg-emerald-500 text-white shadow shadow-emerald-200' : day.isToday ? 'bg-white border-2 border-emerald-400 text-emerald-600' : 'bg-slate-50 border border-slate-100 text-slate-300'}`} data-id="element-8680" data-mp="eloy0ak4jzx5">
                  
                    {day.practiced ? day.questionCount : '·'}
                  </div>
                </div>)}
            </div>
          </Card>
        </section>

        {/* Decay alerts — only when there is something stale */}
        {decayAlerts.length > 0 && <section data-id="element-8681" data-mp="3m6d712o28ld0">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-8682" data-mp="3m5v11eblisrw">
              <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-8683" data-mp="3r3wkw1guiw4f" />
              Aandachtspunt
            </h2>
            <div className="space-y-2" data-id="element-8684" data-mp="3h83b2puihgqc">
              {decayAlerts.map(alert => {
            const IconComp = getModuleIcon(alert.module.icon);
            const weeksAgo = Math.floor(alert.daysSince / 7);
            const timeLabel = weeksAgo >= 4 ? 'meer dan een maand' : weeksAgo >= 2 ? `${weeksAgo} weken` : 'meer dan een week';
            return <Card key={alert.module.id} onClick={() => onNavigate?.('voortgang')} className="p-4 border border-rose-200 bg-rose-50/60 cursor-pointer hover:bg-rose-50 transition-colors flex items-center gap-3 group" data-id="element-8685" data-mp="3v6t9q9wzbogt">
                  
                    <div className="w-10 h-10 rounded-xl bg-white text-rose-600 flex items-center justify-center flex-shrink-0 border border-rose-200" data-id="element-8686" data-mp="rpazxwxnc7dr">
                      <IconComp className="w-5 h-5" data-id="element-8687" data-mp="2h77x416snhpb" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-8688" data-mp="2c9ok14lu1ayy">
                      <div className="font-black text-sm text-rose-900 truncate" data-id="element-8689" data-mp="2c8wofts8jd8o">
                        {alert.module.title} dreigt weg te zakken
                      </div>
                      <div className="text-xs font-medium text-rose-700/80 truncate" data-id="element-8690" data-mp="2m0ar2afhgt61">
                        {timeLabel} niet geoefend — 10 minuten herhalen helpt.
                      </div>
                    </div>
                    <History className="w-4 h-4 text-rose-400 group-hover:translate-x-0.5 transition-transform" data-id="element-8691" data-mp="1tc54di4vcu3k" />
                  </Card>;
          })}
            </div>
          </section>}

        {/* Tip + Latest Assessment — two compact slots side by side */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-8692" data-mp="3ioh1jdz36fs8">
          {topTip && <Card onClick={() => onNavigate?.('tips')} className={`p-5 cursor-pointer hover:shadow-md transition-shadow border bg-white group ${topTip.accent === 'amber' ? 'border-amber-200' : topTip.accent === 'rose' ? 'border-rose-200' : 'border-emerald-200'}`} data-id="element-8693" data-mp="3enx4sbifde6m">
            
              <div className="flex items-start gap-3 mb-3" data-id="element-8694" data-mp="2gdnlb08nzx65">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${topTip.accent === 'amber' ? 'bg-amber-50 text-amber-600' : topTip.accent === 'rose' ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-8695" data-mp="2lc8jku4cw6ry">
                
                  {topTip.accent === 'rose' ? <Target className="w-5 h-5" data-id="element-8696" data-mp="1xm4esjffm0zd" /> : topTip.accent === 'amber' ? <Clock className="w-5 h-5" data-id="element-8697" data-mp="21memc0ivgzgc" /> : <Lightbulb className="w-5 h-5" data-id="element-8698" data-mp="21m4vkaq06o9l" />}
                </div>
                <div className="flex-1 min-w-0" data-id="element-8699" data-mp="26e6hkocz3tct">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8700" data-mp="26ey0ozpdvym7">
                    Tip van Djay
                  </div>
                  <h3 className="text-sm font-black text-slate-900 leading-snug" data-id="element-8701" data-mp="1dwkdrv5l5x1w">
                    {topTip.title}
                  </h3>
                </div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed mb-3" data-id="element-8702" data-mp="11eztroyynjtk">
                {topTip.body}
              </p>
              <div className="flex items-center gap-1 text-xs font-bold text-emerald-600 group-hover:gap-1.5 transition-all" data-id="element-8703" data-mp="xc3g8coa2dwh">
                <span data-id="element-8704" data-mp="sds5lzq5rzd7">Alle tips</span>
                <ArrowRight className="w-3 h-3" data-id="element-8705" data-mp="neytaamlyeem" />
              </div>
            </Card>}

          {isLoadingAssessment ?
        // v1.7 (P-2): skeleton while the latest-assessment fetch is in
        // flight — prevents the "Nog geen toets gemaakt" flash on mount.
        <Card className="p-5 border border-slate-200 bg-white" aria-busy="true" aria-label="Laatste toets laden" data-id="element-8706" data-mp="dpf5cqk1fucw">
            
              <div className="flex items-start gap-3 mb-3" data-id="element-8707" data-mp="4ohaaxrkux6g">
                <Skeleton variant="rect" className="w-10 h-10" data-id="element-8708" data-mp="4nxqfbid9yps" />
                <div className="flex-1 min-w-0 space-y-2" data-id="element-8709" data-mp="3vuih2u4if4ma">
                  <Skeleton className="w-20 h-3" data-id="element-8710" data-mp="3vx4p0uvbbm51" />
                  <Skeleton className="w-32 h-4" data-id="element-8711" data-mp="1zsow5m28ca72" />
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-8712" data-mp="24pozhp7p2yuh">
                <div className="space-y-2" data-id="element-8713" data-mp="1q1u94fh5t6kk">
                  <Skeleton className="w-12 h-3" data-id="element-8714" data-mp="1q119dygmbhbx" />
                  <Skeleton className="w-10 h-5" data-id="element-8715" data-mp="1v0xbtt9o30pd" />
                </div>
                <Skeleton variant="rect" className="w-14 h-9" data-id="element-8716" data-mp="1uz2ne4z6sqgj" />
              </div>
            </Card> : latestAssessment ? <Card onClick={() => onNavigate?.('toetsen')} className="p-5 cursor-pointer hover:shadow-md transition-shadow border border-slate-200 bg-white group" data-id="element-8717" data-mp="1h3iiwek7gasn">
            
              <div className="flex items-start gap-3 mb-3" data-id="element-8718" data-mp="174jamx3cqh66">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-indigo-50 text-indigo-600" data-id="element-8719" data-mp="1c19mf884iqls">
                  <FileText className="w-5 h-5" data-id="element-8720" data-mp="31hiuj2erlxj9" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-8721" data-mp="36gcad1hiy0ml">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-8722" data-mp="36dzqujw4bi7f">
                    Laatste toets
                  </div>
                  <h3 className="text-sm font-black text-slate-900 leading-snug" data-id="element-8723" data-mp="pup5do6k3s1w">
                    {new Date(latestAssessment.completedAt).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'long'
                })}{' '}
                    · Groep {latestAssessment.selectedGroup}
                  </h3>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-8724" data-mp="18kwzxhw6xa6h">
                <div data-id="element-8725" data-mp="1nhe9q4oqegqx">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-8726" data-mp="1nhxr9kagz34f">
                    Niveau
                  </div>
                  <div className="text-lg font-black text-slate-800" data-id="element-8727" data-mp="1ij34k79dsuf1">
                    {latestAssessment.overallEstimatedLevelCode}
                  </div>
                </div>
                {cito && <div className={`flex items-center justify-center px-3 py-2 rounded-xl border-2 ${cito.bgColor} ${cito.borderColor}`} data-id="element-8728" data-mp="3kb9634glgbfi">
                
                    <span className={`text-lg font-black ${cito.color}`} data-id="element-8729" data-mp="hthpns44665h">
                      {cito.label}
                    </span>
                  </div>}
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-8730" data-mp="mv7138v90lu3" />
              </div>
            </Card> : assessmentLoadError ?
        /*
         Mislukt ophalen is GEEN "nog geen toets gemaakt". Die tekst is een
         bewering over het kind, en bij een kind dat wél toetsen heeft
         gemaakt is ze onwaar — zonder dat de ouder aanleiding heeft om te
         twijfelen. Eigen kaart, met een reden en geen conclusie.
         */
        <Card className="p-5 border border-dashed border-slate-200 bg-slate-50/40 flex items-center gap-3" data-id="element-8731" data-mp="1ai6ua14dotlz">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-400" data-id="element-8732" data-mp="1fjmbfi3fqfz3">
                <CloudOff className="w-5 h-5" data-id="element-8733" data-mp="34yx09khvb6rd" />
              </div>
              <div data-id="element-8734" data-mp="35087juaiuwn8">
                <div className="text-xs font-bold text-slate-500" data-id="element-8735" data-mp="34wkesxcwie9k">
                  Toetsgegevens niet opgehaald
                </div>
                <div className="text-[11px] text-slate-400 leading-snug" data-id="element-8736" data-mp="2uzxty8kkrn7j">
                  Er ging iets mis bij het ophalen. Er staat dus niet dat er
                  geen toets is — we weten het even niet.
                </div>
              </div>
            </Card> :
        // Placeholder when there's no assessment yet
        <Card className="p-5 border border-dashed border-slate-200 bg-slate-50/40 flex items-center gap-3" data-id="element-8737" data-mp="3irmpdxsp0rsp">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-400" data-id="element-8738" data-mp="3dxs9l5g7kwyw">
                <FileText className="w-5 h-5" data-id="element-8739" data-mp="aai5vlcqqcll" />
              </div>
              <div data-id="element-8740" data-mp="ac33xky9kdo7">
                <div className="text-xs font-bold text-slate-500" data-id="element-8741" data-mp="accsd43nucrs">
                  Nog geen toets gemaakt
                </div>
                <div className="text-[11px] text-slate-400 leading-snug" data-id="element-8742" data-mp="3a1318gi5cd9r">
                  Zodra {selectedChild.displayName} een toets afrondt verschijnt
                  hier het niveau.
                </div>
              </div>
            </Card>}
        </section>

        {/* Slim explore-row — replaces the duplicate stat-cards. No numbers here:
             this is pure navigation, not a stat-restate. */}
        <section data-id="element-8743" data-mp="3tpc35i9hrxmg">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3" data-id="element-8744" data-mp="3tqwzndfd9qmy">
            Verder kijken
          </h2>
          <div className={`grid gap-3 ${linkedLearners.length >= 2 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`} data-id="element-8745" data-mp="1jozj2ne4v7i">
            
            <button onClick={() => onNavigate?.('voortgang')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 focus-visible:outline-none focus-visible:border-emerald-400 focus-visible:bg-emerald-50/60 focus-visible:ring-2 focus-visible:ring-emerald-300 transition-all text-left group" data-id="element-8746" data-mp="3opps9fh4ytgt">
              
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0" data-id="element-8747" data-mp="20szzponp7ngy">
                <TrendingUp className="w-4 h-4" data-id="element-8748" data-mp="2er7v3s536gq0" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8749" data-mp="29swl9hesk67s">
                <div className="text-sm font-black text-slate-900" data-id="element-8750" data-mp="29ty0ly2ubevb">Vakken</div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8751" data-mp="1m4veoe16azv0">
                  Per-vak beheersing
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-500 transition-colors" data-id="element-8752" data-mp="1i0femte8qmnp" />
            </button>

            <button onClick={() => onNavigate?.('activiteit')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 focus-visible:outline-none focus-visible:border-blue-400 focus-visible:bg-blue-50/60 focus-visible:ring-2 focus-visible:ring-blue-300 transition-all text-left group" data-id="element-8753" data-mp="1r2euhidirkp2">
              
              <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0" data-id="element-8754" data-mp="3slh610rjdkeo">
                <CalendarDays className="w-4 h-4" data-id="element-8755" data-mp="3itbhk10htl48" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8756" data-mp="3irgv2i9kpky1">
                <div className="text-sm font-black text-slate-900" data-id="element-8757" data-mp="3et9djl7acfls">
                  Activiteit
                </div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8758" data-mp="fckxii82t7jj">
                  Kalender & reeksen
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" data-id="element-8759" data-mp="6cep3ar8ixr8" />
            </button>

            <button onClick={() => onNavigate?.('tips')} className={`flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/40 focus-visible:outline-none focus-visible:border-amber-400 focus-visible:bg-amber-50/60 focus-visible:ring-2 focus-visible:ring-amber-300 transition-all text-left group ${linkedLearners.length >= 2 ? '' : 'col-span-2 sm:col-span-1'}`} data-id="element-8760" data-mp="1e4tiz1llv53">
              
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0" data-id="element-8761" data-mp="16kusbpo92xv7">
                <Lightbulb className="w-4 h-4" data-id="element-8762" data-mp="1fcwqfhauk22b" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-8763" data-mp="1fe7uzjc49duh">
                <div className="text-sm font-black text-slate-900" data-id="element-8764" data-mp="1kcj4fsyi1mc6">Tips</div>
                <div className="text-xs text-slate-500 truncate" data-id="element-8765" data-mp="1kedu1kkpu2ml">
                  Alle adviezen
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-amber-500 transition-colors" data-id="element-8766" data-mp="1u9xhrjp4lv09" />
            </button>

            {/* W4-1 — Vergelijken: only surfaces for multi-child accounts (Mara persona) */}
            {linkedLearners.length >= 2 && <button onClick={() => onNavigate?.('compare')} className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/40 focus-visible:outline-none focus-visible:border-violet-400 focus-visible:bg-violet-50/60 focus-visible:ring-2 focus-visible:ring-violet-300 transition-all text-left group" data-id="element-8767" data-mp="3w3wtu7a2oge0">
              
                <div className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center flex-shrink-0" data-id="element-8768" data-mp="1gaoj5dmubplm">
                  <Users className="w-4 h-4" data-id="element-8769" data-mp="1q7jczj8651tk" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-8770" data-mp="1q5ou0aggjjs4">
                  <div className="text-sm font-black text-slate-900" data-id="element-8771" data-mp="1l8yfhrkaww8t">
                    Vergelijken
                  </div>
                  <div className="text-xs text-slate-500 truncate" data-id="element-8772" data-mp="1zw1b1sp2cuti">
                    Kinderen naast elkaar
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-violet-500 transition-colors" data-id="element-8773" data-mp="28y0vkuzaeh17" />
              </button>}
          </div>
        </section>
      </div>
    </PageLayout>;
});